import { memo, useId } from 'react';
import { HAIR_COLORS, SKIN_TONES, type FaceSpec } from '@shared/types';

/**
 * Retrato del jugador dibujado por código (SVG, sin imágenes): fondo y
 * camiseta del color del equipo, cuello, orejas, cara (4 formas), cabello (8
 * estilos que asoman bajo la gorra), vello facial, cejas, ojos con brillo,
 * nariz, boca y la gorra del equipo con su visera. Ningún parecido con
 * jugadores reales: las combinaciones salen del código del rostro.
 */
interface PortraitProps {
  face: FaceSpec;
  /** Color principal del equipo (camiseta, gorra). */
  color: string;
  /** Segundo color (bandera): franja de la gorra y cuello de la camiseta. */
  color2?: string;
  /** Dorsal en el pecho (opcional). */
  dorsal?: number;
  size?: number;
  /** Con casco de bateo en lugar de gorra. */
  helmet?: boolean;
  className?: string;
  title?: string;
}

const shade = (hex: string, k: number): string => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v * k)));
  return `#${[ch((n >> 16) & 255), ch((n >> 8) & 255), ch(n & 255)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/** Contorno de la cara (centro 50,52): ovalada, redonda, cuadrada, alargada. */
const HEAD: string[] = [
  'M50 22 C66 22 72 34 72 50 C72 68 62 80 50 80 C38 80 28 68 28 50 C28 34 34 22 50 22 Z',
  'M50 23 C68 23 74 36 74 51 C74 68 64 79 50 79 C36 79 26 68 26 51 C26 36 32 23 50 23 Z',
  'M50 22 C66 22 72 32 72 48 C72 62 70 74 60 79 C55 81 45 81 40 79 C30 74 28 62 28 48 C28 32 34 22 50 22 Z',
  'M50 20 C64 20 70 33 70 50 C70 69 61 82 50 82 C39 82 30 69 30 50 C30 33 36 20 50 20 Z',
];

export const Portrait = memo(({ face, color, color2, dorsal, size = 64, helmet = false, className, title }: PortraitProps) => {
  const id = useId().replace(/:/g, '');
  const skin = SKIN_TONES[face.skin] ?? SKIN_TONES[2];
  const skinDark = shade(skin, 0.82);
  const hair = HAIR_COLORS[face.hairColor] ?? HAIR_COLORS[0];
  const hairHi = shade(hair, 1.6);
  const cap = color;
  const capDark = shade(color, 0.7);
  const trim = color2 ?? shade(color, 1.35);
  const shapeIdx = face.shape % HEAD.length;
  const jaw = shapeIdx === 2 ? 76 : shapeIdx === 3 ? 79 : 76;
  // Ojos: forma y separación por tipo.
  const eyeY = 52;
  const eyeDx = face.eyes === 2 ? 10 : 9;
  const eyeRy = face.eyes === 1 ? 2.2 : face.eyes === 3 ? 3.2 : 2.7;
  const browTilt = [0, 2, -1.5, 1][face.brows] ?? 0;
  const browW = face.brows === 3 ? 2.8 : 2.1;
  const mouthPath = [
    'M43 68 Q50 73 57 68', // sonrisa
    'M44 69 L56 69', // seria
    'M43 69 Q51 72 57 66', // media sonrisa
    'M43 67 Q50 75 57 67 Q50 70 43 67 Z', // sonrisa abierta
  ][face.mouth] ?? 'M43 68 Q50 73 57 68';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      role="img"
      aria-label={title ?? 'Retrato del jugador'}
      data-face={`${face.skin}-${face.hair}-${face.hairColor}-${face.beard}`}
    >
      <defs>
        <radialGradient id={`bg${id}`} cx="50%" cy="35%" r="70%">
          <stop offset="0%" stopColor={shade(color, 0.55)} />
          <stop offset="100%" stopColor={shade(color, 0.18)} />
        </radialGradient>
        <radialGradient id={`sk${id}`} cx="45%" cy="40%" r="65%">
          <stop offset="0%" stopColor={shade(skin, 1.08)} />
          <stop offset="100%" stopColor={skinDark} />
        </radialGradient>
        <clipPath id={`cl${id}`}>
          <circle cx="50" cy="50" r="50" />
        </clipPath>
      </defs>
      <g clipPath={`url(#cl${id})`}>
        <rect width="100" height="100" fill={`url(#bg${id})`} />
        {/* Cabello largo por detrás (trenzas, largo, afro) */}
        {face.hair === 3 && <path d="M26 46 C24 66 28 84 36 90 L64 90 C72 84 76 66 74 46 Z" fill={hair} />}
        {face.hair === 6 && (
          <g fill={hair}>
            {[30, 36, 64, 70].map((x) => (
              <rect key={x} x={x - 2.5} y={46} width={5} height={36} rx={2.5} />
            ))}
          </g>
        )}
        {face.hair === 4 && <ellipse cx="50" cy="40" rx="31" ry="27" fill={hair} />}
        {/* Hombros y camiseta */}
        <path d="M10 100 C12 84 26 78 40 76 L60 76 C74 78 88 84 90 100 Z" fill={shade(color, 0.92)} />
        <path d="M40 76 L50 88 L60 76" fill="none" stroke={trim} strokeWidth={3.5} strokeLinejoin="round" />
        {dorsal !== undefined && (
          <text x="70" y="96" fontSize="12" fontWeight="800" fill="#ffffff" fillOpacity={0.9} textAnchor="middle" fontFamily="system-ui, sans-serif">
            {dorsal}
          </text>
        )}
        {/* Cuello */}
        <path d="M42 70 L42 80 Q50 84 58 80 L58 70 Z" fill={skinDark} />
        {/* Orejas */}
        <ellipse cx="27.5" cy="53" rx="3.6" ry="6" fill={skinDark} />
        <ellipse cx="72.5" cy="53" rx="3.6" ry="6" fill={skinDark} />
        {/* Cara */}
        <path d={HEAD[shapeIdx]} fill={`url(#sk${id})`} />
        {/* Patillas / laterales del pelo (bajo la gorra) */}
        {face.hair !== 5 && face.hair !== 1 && face.hair !== 4 && (
          <g fill={hair}>
            <path d="M28 40 Q27 50 29 56 L32 56 Q31 47 33 40 Z" />
            <path d="M72 40 Q73 50 71 56 L68 56 Q69 47 67 40 Z" />
          </g>
        )}
        {face.hair === 1 && <path d="M29 40 Q28 47 30 52 L32 52 Q31 46 33 40 Z M71 40 Q72 47 70 52 L68 52 Q69 46 67 40 Z" fill={hair} fillOpacity={0.55} />}
        {face.hair === 2 && (
          <g fill={hair}>
            {[29, 33, 67, 71].map((x, i) => (
              <circle key={i} cx={x} cy={41 + (i % 2) * 4} r={3.2} />
            ))}
          </g>
        )}
        {face.hair === 4 && (
          <g fill={hair}>
            <circle cx="25" cy="46" r="7" />
            <circle cx="75" cy="46" r="7" />
          </g>
        )}
        {/* Vello facial */}
        {face.beard === 1 && <path d={`M32 58 Q34 ${jaw} 50 ${jaw + 2} Q66 ${jaw} 68 58 Q62 70 50 72 Q38 70 32 58 Z`} fill={hair} fillOpacity={0.28} />}
        {face.beard === 2 && <path d="M41 64.5 Q50 61 59 64.5 Q57 67 50 66 Q43 67 41 64.5 Z" fill={hair} />}
        {face.beard === 3 && (
          <g fill={hair}>
            <path d="M42 64.5 Q50 61.5 58 64.5 Q56 66.5 50 65.5 Q44 66.5 42 64.5 Z" />
            <path d="M45 71 Q50 70 55 71 L54 77 Q50 80 46 77 Z" />
          </g>
        )}
        {face.beard === 4 && (
          <path
            d={`M30 54 Q31 ${jaw - 2} 50 ${jaw + 4} Q69 ${jaw - 2} 70 54 Q66 62 60 64 Q55 62 50 62.5 Q45 62 40 64 Q34 62 30 54 Z M44 69 Q50 72.5 56 69 Q55 66.5 50 67 Q45 66.5 44 69 Z`}
            fill={hair}
            fillRule="evenodd"
          />
        )}
        {/* Cejas */}
        <g stroke={shade(hair, face.hairColor >= 4 ? 0.6 : 1)} strokeWidth={browW} strokeLinecap="round" fill="none">
          <path d={`M${50 - eyeDx - 5} ${eyeY - 6 + browTilt} Q${50 - eyeDx} ${eyeY - 8.5} ${50 - eyeDx + 5} ${eyeY - 6.5}`} />
          <path d={`M${50 + eyeDx - 5} ${eyeY - 6.5} Q${50 + eyeDx} ${eyeY - 8.5} ${50 + eyeDx + 5} ${eyeY - 6 + browTilt}`} />
        </g>
        {/* Ojos (blanco, iris, brillo) */}
        {[-1, 1].map((s) => (
          <g key={s}>
            <ellipse cx={50 + s * eyeDx} cy={eyeY} rx={4} ry={eyeRy} fill="#fbfbf8" />
            <circle cx={50 + s * eyeDx + 0.4} cy={eyeY + 0.2} r={Math.min(2.3, eyeRy)} fill={face.skin <= 1 && face.eyes === 2 ? '#3d6b8f' : '#3a2617'} />
            <circle cx={50 + s * eyeDx + 1.1} cy={eyeY - 0.7} r={0.7} fill="#ffffff" />
            <path d={`M${50 + s * eyeDx - 4.2} ${eyeY - 0.6} Q${50 + s * eyeDx} ${eyeY - eyeRy - 1.4} ${50 + s * eyeDx + 4.2} ${eyeY - 0.6}`} fill="none" stroke={shade(skin, 0.55)} strokeWidth={0.9} />
          </g>
        ))}
        {/* Nariz */}
        <path d="M50 54 Q47.5 61 46.5 62.5 Q50 64.5 53.5 62.5" fill="none" stroke={shade(skin, 0.68)} strokeWidth={1.4} strokeLinecap="round" />
        {/* Boca */}
        <path d={mouthPath} fill={face.mouth === 3 ? '#7a2d2a' : 'none'} stroke="#7a3b32" strokeWidth={1.8} strokeLinecap="round" />
        {/* Gorra del equipo (o casco de bateo) */}
        {helmet ? (
          <g>
            <path d="M24 46 C24 24 38 14 50 14 C64 14 76 24 76 46 L70 46 C68 34 62 28 50 28 C38 28 32 34 30 46 Z" fill={cap} />
            <path d="M24 46 C24 24 38 14 50 14 C64 14 76 24 76 46" fill="none" stroke={capDark} strokeWidth={1.5} />
            <path d="M68 40 Q80 44 80 56 Q76 60 72 58 Q72 48 66 46 Z" fill={capDark} />
            <ellipse cx="44" cy="22" rx="8" ry="3" fill="#ffffff" fillOpacity={0.35} />
          </g>
        ) : (
          <g>
            <path d="M27 42 C27 22 39 16 50 16 C61 16 73 22 73 42 Z" fill={cap} />
            <path d="M50 16 L50 42" stroke={capDark} strokeWidth={1} />
            <path d="M27 40 L73 40 L73 43 L27 43 Z" fill={trim} />
            <path d="M24 43 Q50 34 76 43 Q74 48 50 46 Q26 48 24 43 Z" fill={capDark} />
            <circle cx="50" cy="16.5" r="1.8" fill={capDark} />
            {/* Pelo que asoma por delante (cresta: por encima no; afro: lados) */}
            {face.hair === 7 && <path d="M47 16 L50 6 L53 16 Z" fill={hair} />}
            <ellipse cx="42" cy="24" rx="7" ry="3" fill="#ffffff" fillOpacity={0.18} />
          </g>
        )}
        {/* Brillo del pelo en estilos con volumen */}
        {(face.hair === 3 || face.hair === 4) && <path d="M30 52 Q32 60 35 64" stroke={hairHi} strokeWidth={1} fill="none" strokeOpacity={0.5} />}
      </g>
    </svg>
  );
});
Portrait.displayName = 'Portrait';
